import AppsIcon from '@material-design-icons/svg/filled/apps.svg?react';
import { Field, Section } from '../../../components/common/detailParts';
import { S3Image } from '../../../components/S3Image';
import { EmptyState } from '../../../components/common/detailListParts';
import type { SuperDappDetailData, SuperDappTabProps } from '../types';

type Dapp = SuperDappDetailData['dapp'];

/** Wide graphics, de-duplicated: the editor's choice graphic often IS the feature graphic. */
function graphicTiles(dapp: Dapp) {
  const seen = new Set<string>();
  return [
    { label: 'Banner', url: dapp.bannerUrl },
    { label: 'Feature graphic', url: dapp.featureGraphicUrl },
    { label: "Editor's choice graphic", url: dapp.editorsChoiceGraphicUrl },
  ].filter((tile): tile is { label: string; url: string } => {
    if (!tile.url || seen.has(tile.url)) return false;
    seen.add(tile.url);
    return true;
  });
}

export function mediaCount(dapp: Dapp): number {
  return (
    (dapp.dappIconUrl ? 1 : 0) +
    graphicTiles(dapp).length +
    dapp.dappPreviewUrls.length
  );
}

export function MediaTab({ data }: SuperDappTabProps) {
  const { dapp } = data;
  const tiles = graphicTiles(dapp);
  const screenshots = dapp.dappPreviewUrls;

  if (mediaCount(dapp) === 0) {
    return (
      <Section title="Media">
        <EmptyState message="No media uploaded" />
      </Section>
    );
  }

  return (
    <div className="space-y-4">
      <Section title="Listing graphics">
        <div className="grid gap-5 sm:grid-cols-[auto_minmax(0,1fr)]">
          <Field label="App icon">
            <S3Image
              src={dapp.dappIconUrl}
              thumbnailSrc={dapp.dappIconThumbnailUrl}
              useThumbnail={false}
              alt={`${dapp.dappName} icon`}
              className="size-24 rounded-2xl object-cover ring-1 ring-white/10"
              fallback={
                <div className="flex size-24 items-center justify-center rounded-2xl bg-white/[0.04] ring-1 ring-white/10">
                  <AppsIcon className="size-8 fill-current text-neutral-500" />
                </div>
              }
            />
          </Field>
          {tiles.length > 0 && (
            <div className="grid gap-5 md:grid-cols-2">
              {tiles.map((tile) => (
                <Field key={tile.label} label={tile.label}>
                  <div className="flex aspect-[16/9] items-center justify-center overflow-hidden rounded-xl border border-white/5 bg-black/20">
                    <S3Image
                      src={tile.url}
                      alt={tile.label}
                      className="size-full object-contain"
                      fallback={
                        <span className="text-[10.5px] text-neutral-600">
                          Unavailable
                        </span>
                      }
                    />
                  </div>
                </Field>
              ))}
            </div>
          )}
        </div>
      </Section>

      {screenshots.length > 0 && (
        <Section
          title="Screenshots"
          trailing={
            <span className="text-xs tabular-nums text-neutral-500">
              {screenshots.length}
            </span>
          }
        >
          <div className="scrollbar-hide flex gap-3 overflow-x-auto">
            {screenshots.map((url, index) => (
              <S3Image
                key={url}
                src={url}
                alt={`Screenshot ${index + 1}`}
                className="h-64 w-auto shrink-0 rounded-xl border border-white/5 object-cover"
                fallback={
                  <div className="flex h-64 w-32 shrink-0 items-center justify-center rounded-xl border border-white/5 bg-white/[0.02] text-[10.5px] text-neutral-600">
                    Unavailable
                  </div>
                }
              />
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}
